/**
 * Release Line
 *
 * The fleet-wide unit of mobile versioning: one line per `runtimeVersion` (1.8, 1.9),
 * stored in the `AppVersion` table. A line is NOT a marketing version. Store patch
 * strings drift per app (1.8.17, 1.8.23, "5 (1.8.17)"), and the code only differs
 * between runtimes, so marketing bumps, per-school OTAs and rebuilds never create a
 * line (R4); they are recorded against the line they shipped on.
 *
 * `runtimeVersion` is the exact string in `apps/mobile/version.json` ("1.8.0", not
 * "1.8"), and the line's display `name` ("1.8") is derived from it unless given.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-23-2135-feat-app-release-lines-and-school-drafts-plan.md (U1, U3)
 */

import { z } from 'zod'
import { StoreListingStoreSchema } from './mobile-app-store-listing'
import { StoreScreenshotDeviceClassSchema } from './mobile-app-store-screenshot'
import {
  compareVersions,
  isStoreVersionInFlight,
  STORE_VERSION_STALENESS_MS,
  StoreVersionStateSchema,
  type MobileAppStoreVersion,
  type StoreVersionLane,
  type StoreVersionState,
  type StoreVersionThresholds
} from './mobile-deployment'
import { PlatformSchema, type Platform } from './platform'

/**
 * Where a line is in its life. A string column in the database, closed here.
 *
 *  - `upcoming`: being prepared; apps may be building or in review on it.
 *  - `current`: the line the fleet ships on.
 *  - `sunsetting`: still live on apps and still gets backport OTAs, but no new apps.
 *  - `retired`: no longer shipped to. Older runtimes (1.5, 1.7) are seeded here so
 *    their apps show a retired line instead of `unknown`.
 */
export const RELEASE_LINE_LIFECYCLES = ['upcoming', 'current', 'sunsetting', 'retired'] as const
export const ReleaseLineLifecycleSchema = z.enum(RELEASE_LINE_LIFECYCLES)
export type ReleaseLineLifecycle = z.infer<typeof ReleaseLineLifecycleSchema>

/** `major.minor.patch`, digits only — the shape `version.json` carries. */
export const RELEASE_LINE_RUNTIME_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/

export const ReleaseLineRuntimeVersionSchema = z
  .string()
  .regex(RELEASE_LINE_RUNTIME_PATTERN, 'runtimeVersion must be major.minor.patch, e.g. 1.9.0')

/** "1.8.0" → "1.8". The display name a line gets when none is given. */
export function releaseLineNameFromRuntime(runtimeVersion: string): string {
  const [major, minor] = runtimeVersion.split('.')
  return `${major}.${minor}`
}

/**
 * The line runtime a marketing version string belongs to, or `null` when it carries
 * no readable `major.minor`.
 *
 * `versioning.md` keeps the marketing `major.minor` aligned with the runtime, so
 * "1.8.25" maps to "1.8.0". Tolerates the messy store strings Play reports
 * ("5 (1.8.17)", "v1.8.9") by reading the first dotted number in the string. This is
 * an INFERENCE: a caller that stores it must label it as such (KTD2).
 */
export function releaseLineRuntimeFromMarketingVersion(version: string | null | undefined): string | null {
  if (!version) return null
  const match = /(\d{1,4})\.(\d{1,4})(?:\.\d{1,9})?/.exec(version)
  if (!match) return null
  return `${Number(match[1])}.${Number(match[2])}.0`
}

/**
 * The plain marketing version inside a store version string ("5 (1.8.17)" and
 * "v1.8.17" both read "1.8.17"), or `null` when it carries no dotted number.
 * The first dotted number wins, as in `releaseLineRuntimeFromMarketingVersion`.
 */
export function marketingVersionFromStoreString(version: string | null | undefined): string | null {
  if (!version) return null
  const match = /\d{1,4}\.\d{1,4}(?:\.\d{1,9})?/.exec(version)
  return match ? match[0] : null
}

export const ReleaseLineSchema = z.object({
  id: z.string(),
  runtimeVersion: z.string(),
  name: z.string(),
  gitRef: z.string().nullable(),
  lifecycle: ReleaseLineLifecycleSchema,
  isDefault: z.boolean(),
  /** Fleet-wide "what's new"; a `{schoolName}` token is filled per app at release. */
  whatsNew: z.string().nullable(),
  /** The device classes this line's screenshot sets must cover. `null` = not decided yet. */
  screenshotDeviceClasses: z.array(StoreScreenshotDeviceClassSchema).nullable(),
  releaseNotes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable()
})
export type ReleaseLine = z.infer<typeof ReleaseLineSchema>

// =============================================================================
// Each app's position on each line (U3, R5-R9, KTD3)
//
// The app × line record (`MobileAppReleaseLine`, one row per app, line and
// platform) stores ONLY what cannot be derived: planned intent, a blocked
// reason, and what last shipped. The state below is derived at read, from the
// app's store versions tied to a line by their resolved runtime (U2, KTD2), so
// the staff board, the school status and `pnpm sj` read one answer.
// =============================================================================

/**
 * Where one app sits on one line, per platform (R5).
 *
 *  - `planned`: the app has a record for the line and nothing in the store yet.
 *  - `blocked`: planned, with a per-app prerequisite missing (the record's reason).
 *  - `preparing` / `in_review` / `rejected`: a store version on this line is in flight.
 *  - `live`: the store serves a version on this line (a staged rollout counts, with a caveat).
 *  - `superseded`: a newer line has gone live on this platform, or the store closed this line.
 *  - `unknown`: the evidence cannot say. Its own state, never folded into a count.
 */
export const APP_LINE_STATES = [
  'planned',
  'blocked',
  'preparing',
  'in_review',
  'rejected',
  'live',
  'superseded',
  'unknown'
] as const
export const AppLineStateSchema = z.enum(APP_LINE_STATES)
export type AppLineState = z.infer<typeof AppLineStateSchema>

/**
 * Why the derivation answered what it did. One closed code per answer, so a
 * board can say "unknown: store not read for 3 days" rather than "unknown".
 */
export const APP_LINE_STATE_REASONS = [
  /** planned / blocked: no store version on this line yet. */
  'no_store_version',
  /** planned / blocked: the line's only store versions were withdrawn. */
  'store_withdrawn',
  /** preparing / in_review / rejected: the newest in-flight store version on the line. */
  'store_in_flight',
  /** live: fully released. */
  'store_live',
  /** live, with a caveat: a staged rollout; `rolloutFraction` says how far. */
  'store_rolling_out',
  /** superseded: a newer line is fully live on this platform (R7). */
  'newer_line_live',
  /** superseded: the store replaced every version of this line and nothing newer is readable as live. */
  'line_closed',
  /** unknown: a live or in-flight store version has no resolved runtime, and it could be this line's. */
  'unresolved_runtime',
  /** unknown: the last successful store read is older than the staleness threshold. */
  'stale_observation',
  /** unknown: nothing has ever successfully read this store for this app. */
  'never_observed',
  /** unknown: the last store check failed; `reasonText` carries why. */
  'unreadable',
  /** unknown: this store lane has no meaning for this app. */
  'not_applicable',
  /** unknown: the line's newest version is a halted rollout, which is neither live nor in flight. */
  'halted',
  /** unknown: the store reported a state we have no mapping for; `reasonText` carries its word. */
  'unmapped_store_state',
  /** unknown: no planned record for this line and nothing observed on it. */
  'no_record'
] as const
export const AppLineStateReasonSchema = z.enum(APP_LINE_STATE_REASONS)
export type AppLineStateReason = z.infer<typeof AppLineStateReasonSchema>

/** One store version, said as the version string and build number a person reads. */
export const AppLineVersionRefSchema = z.object({
  versionString: z.string().optional(),
  buildNumber: z.string().optional(),
  storeState: StoreVersionStateSchema
})
export type AppLineVersionRef = z.infer<typeof AppLineVersionRefSchema>

/** What last shipped on this app, line and platform, copied from the store (R8). */
export const AppLineShippedSchema = z.object({
  versionString: z.string().nullable(),
  buildNumber: z.string().nullable(),
  shippedAt: z.coerce.date().nullable()
})
export type AppLineShipped = z.infer<typeof AppLineShippedSchema>

// -----------------------------------------------------------------------------
// Store drift (U15, R24)
// -----------------------------------------------------------------------------

/**
 * The store text a drift check compares: the shipped snapshot's listing
 * columns, plus what's new where the store exposes it for the version (Apple).
 * Screenshots are not compared; see `AppLineDriftSchema`.
 */
export const STORE_DRIFT_FIELDS = [
  'title',
  'subtitle',
  'shortDescription',
  'description',
  'keywords',
  'promotionalText',
  'marketingUrl',
  'supportUrl',
  'privacyPolicyUrl',
  'contactEmail',
  'whatsNew'
] as const
export const StoreDriftFieldSchema = z.enum(STORE_DRIFT_FIELDS)
export type StoreDriftField = z.infer<typeof StoreDriftFieldSchema>

/** The longest snapshot or live value a drifted field keeps, in characters. */
export const STORE_DRIFT_VALUE_MAX = 200

/** One field that differs: what the snapshot says was sent, and what the store holds now. Both truncated. */
export const StoreDriftFieldDiffSchema = z.object({
  field: StoreDriftFieldSchema,
  snapshot: z.string().nullable(),
  live: z.string().nullable()
})
export type StoreDriftFieldDiff = z.infer<typeof StoreDriftFieldDiffSchema>

/**
 * Why a drift check could not read the store. `quota_exceeded` is Play's daily
 * edit-creation budget, kept apart from `store_error` because it clears by
 * itself at midnight UTC and is ours, not a fact about the school's app.
 */
export const STORE_DRIFT_UNREADABLE_CODES = [
  'not_configured',
  'no_credentials',
  'quota_exceeded',
  'version_not_found',
  'store_error'
] as const
export const StoreDriftUnreadableCodeSchema = z.enum(STORE_DRIFT_UNREADABLE_CODES)
export type StoreDriftUnreadableCode = z.infer<typeof StoreDriftUnreadableCodeSchema>

const DriftComparedSchema = z.object({
  checkedAt: z.coerce.date(),
  /** The fields the snapshot holds a value for, which are the only ones compared. */
  comparedFields: z.array(StoreDriftFieldSchema),
  /** The App Store version read (iOS); null on Play, which has one listing. */
  versionString: z.string().nullable(),
  /**
   * False today on both stores: neither read exposes a per-image identity that
   * matches the snapshot's SHA-256 without downloading every image.
   */
  imagesCompared: z.boolean()
})

/**
 * The last drift check of one app × line × platform (`MobileAppReleaseLine.driftResult`).
 *
 *  - `in_sync`: every compared field matches the snapshot.
 *  - `drifted`: at least one field differs; `fields` names each.
 *  - `unreadable`: the store could not be read. Never read as "no drift".
 *  - `not_applicable`: nothing to compare: no snapshot, or no text in it for this store.
 */
export const AppLineDriftSchema = z.discriminatedUnion('status', [
  DriftComparedSchema.extend({ status: z.literal('in_sync') }),
  DriftComparedSchema.extend({ status: z.literal('drifted'), fields: z.array(StoreDriftFieldDiffSchema).min(1) }),
  z.object({
    status: z.literal('unreadable'),
    checkedAt: z.coerce.date(),
    code: StoreDriftUnreadableCodeSchema,
    reason: z.string()
  }),
  z.object({ status: z.literal('not_applicable'), checkedAt: z.coerce.date(), reason: z.string() })
])
export type AppLineDrift = z.infer<typeof AppLineDriftSchema>
export type AppLineDriftStatus = AppLineDrift['status']

/** The derivation's answer for one app, one line and one platform. */
export const AppLineStateResultSchema = z.object({
  /** The line's public id (`AppVersion.publicId`). */
  lineId: z.string(),
  runtimeVersion: z.string(),
  platform: PlatformSchema,
  state: AppLineStateSchema,
  reason: AppLineStateReasonSchema,
  /** The blocked reason, the failed lane's message, or the unmapped vendor word. */
  reasonText: z.string().optional(),
  /** Set only for a live line that is still a staged rollout. */
  rolloutFraction: z.number().optional(),
  /** The newest live (or rolling-out) store version on this line, as observed now. */
  live: AppLineVersionRefSchema.optional(),
  /**
   * The newest preparing, in-review or rejected store version on this line. Set
   * whatever the line's state, so a line live on 1.8.23 with 1.8.25 in review
   * (a staff canary on a few schools) reads "live 1.8.23, in review 1.8.25".
   */
  inFlight: AppLineVersionRefSchema.optional(),
  /** The record's shipped values (R8). Kept after the line is superseded (R9). */
  shipped: AppLineShippedSchema.optional(),
  /** The record's last store drift check (U15, R24). Absent when it was never checked. */
  drift: AppLineDriftSchema.optional()
})
export type AppLineStateResult = z.infer<typeof AppLineStateResultSchema>

/**
 * A platform's current or upcoming line (R6). `unknown` is its own answer:
 * with an unreadable store or an unresolved live build, "no current line"
 * would be a claim nobody can back.
 */
export const AppLinePositionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('line'), lineId: z.string(), runtimeVersion: z.string(), state: AppLineStateSchema }),
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('unknown'), reason: AppLineStateReasonSchema })
])
export type AppLinePosition = z.infer<typeof AppLinePositionSchema>

/** One app and platform across every line it has a record or a store version on. */
export const AppPlatformLinesSchema = z.object({
  platform: PlatformSchema,
  /** Newest runtime first. */
  lines: z.array(AppLineStateResultSchema),
  /** The newest line live in the store on this platform. */
  current: AppLinePositionSchema,
  /** The newest line above `current` that is preparing, in review or rejected. */
  upcoming: AppLinePositionSchema
})
export type AppPlatformLines = z.infer<typeof AppPlatformLinesSchema>

// -----------------------------------------------------------------------------
// The shipped snapshot (R9, R22)
// -----------------------------------------------------------------------------

/**
 * The store text as it was sent, one entry per store and locale. Mirrors the
 * `MobileAppStoreListing` columns a school edits; null means the listing had
 * no value, exactly as the listing row does.
 */
export const AppLineShippedStoreTextSchema = z.object({
  store: StoreListingStoreSchema,
  locale: z.string(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  shortDescription: z.string().nullable(),
  description: z.string().nullable(),
  keywords: z.string().nullable(),
  promotionalText: z.string().nullable(),
  marketingUrl: z.string().nullable(),
  supportUrl: z.string().nullable(),
  privacyPolicyUrl: z.string().nullable(),
  /**
   * Play's contact email, which the Play listing push sends. Optional: baseline
   * and early release snapshots were written without it, and absent reads as
   * "not recorded", never as drift. Play's contact phone is not here: it is the
   * fleet constant `PLAY_CONTACT_PHONE`, not a listing column.
   */
  contactEmail: z.string().nullable().optional()
})
export type AppLineShippedStoreText = z.infer<typeof AppLineShippedStoreTextSchema>

/**
 * `baseline`: seeded from the listing row when the current-line records were
 * first created (U3), so text nobody has changed reads live from day one. It
 * records what the listing SAID then, not what a release sent.
 * `release`: frozen by a staff release from what was actually sent (U13).
 */
export const APP_LINE_SNAPSHOT_KINDS = ['baseline', 'release'] as const
export const AppLineSnapshotKindSchema = z.enum(APP_LINE_SNAPSHOT_KINDS)
export type AppLineSnapshotKind = z.infer<typeof AppLineSnapshotKindSchema>

/** The part every snapshot has: a baseline row carries only this. */
const AppLineShippedSnapshotBaseSchema = z.object({
  kind: AppLineSnapshotKindSchema,
  capturedAt: z.coerce.date(),
  storeText: z.array(AppLineShippedStoreTextSchema).optional()
})

// --- The parts a staff release freezes (U13, R22, KTD9) ----------------------

/**
 * The steps of a release, in the order they run. The Apple rows, the Play
 * pushes, applying the build drafts and the two dispatches. A step's outcome
 * is what the push itself reported, never what was asked for.
 */
export const LINE_RELEASE_STEPS = [
  'apple.app-info',
  'apple.listing',
  'apple.screenshots',
  'play.listing',
  'play.images',
  'apply-drafts',
  'dispatch-build',
  'dispatch-ota'
] as const
export const LineReleaseStepSchema = z.enum(LINE_RELEASE_STEPS)
export type LineReleaseStep = z.infer<typeof LineReleaseStepSchema>

/**
 * `committed`: the step's payload landed (a row that also reports a gap, such
 * as a missing optional field, is still committed and says so in `detail`).
 * `failed`: nothing of the step is known to have landed. `skipped`: an earlier
 * step failed, so this one never ran.
 */
export const LINE_RELEASE_STEP_OUTCOMES = ['committed', 'failed', 'skipped'] as const
export const LineReleaseStepOutcomeSchema = z.enum(LINE_RELEASE_STEP_OUTCOMES)
export type LineReleaseStepOutcome = z.infer<typeof LineReleaseStepOutcomeSchema>

export const LineReleaseStepResultSchema = z.object({
  step: LineReleaseStepSchema,
  outcome: LineReleaseStepOutcomeSchema,
  /**
   * The push row's own verdict. False on a committed step that still reports a
   * problem an operator must fix (Apple app info sent without a privacy URL).
   */
  ok: z.boolean(),
  detail: z.string(),
  at: z.coerce.date()
})
export type LineReleaseStepResult = z.infer<typeof LineReleaseStepResultSchema>

/** A release's kind: push store content, rebuild, or ship an over-the-air update. */
export const LINE_RELEASE_KINDS = ['store', 'build', 'ota'] as const
export const LineReleaseKindSchema = z.enum(LINE_RELEASE_KINDS)
export type LineReleaseKind = z.infer<typeof LineReleaseKindSchema>

/**
 * `complete`: every step committed. `partial`: some committed and a later one
 * failed (`failedStep` names it). `failed`: nothing committed.
 * `in_progress`: the action is still running, or died while it ran.
 */
export const LINE_RELEASE_STATUSES = ['in_progress', 'complete', 'partial', 'failed'] as const
export const LineReleaseStatusSchema = z.enum(LINE_RELEASE_STATUSES)
export type LineReleaseStatus = z.infer<typeof LineReleaseStatusSchema>

/** The last release on this app, line and platform, with every step's outcome. */
export const AppLineReleaseRecordSchema = z.object({
  id: z.string(),
  kind: LineReleaseKindSchema,
  status: LineReleaseStatusSchema,
  startedAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  /** Who released, as the operations event names them. */
  actorName: z.string().nullable(),
  failedStep: LineReleaseStepSchema.nullable(),
  steps: z.array(LineReleaseStepResultSchema)
})
export type AppLineReleaseRecord = z.infer<typeof AppLineReleaseRecordSchema>

/**
 * One image as it was sent: its Cloudinary reference AND the SHA-256 of the
 * bytes the push uploaded. The reference says where it came from; the hash
 * says what it was, which a Cloudinary reference alone cannot promise (an
 * overwritten public id serves new bytes at every version; see U13's note in
 * the release service).
 *
 * `role` is the screenshot device class, or the slot for single-instance art
 * (`app-icon`, `play-feature-graphic`).
 */
export const AppLineShippedAssetSchema = z.object({
  role: z.string(),
  position: z.number().int(),
  cloudinaryPublicId: z.string(),
  cloudinaryVersion: z.string(),
  sha256: z.string().nullable()
})
export type AppLineShippedAsset = z.infer<typeof AppLineShippedAssetSchema>

/** The images one store listing received, and the line whose set they were. */
export const AppLineShippedImagesSchema = z.object({
  store: StoreListingStoreSchema,
  locale: z.string(),
  /** `AppVersion.publicId` of the line whose screenshot set was sent. */
  lineId: z.string(),
  assets: z.array(AppLineShippedAssetSchema)
})
export type AppLineShippedImages = z.infer<typeof AppLineShippedImagesSchema>

/**
 * `sent`: the store accepted it. `refused`: the store refused the text and the
 * rest of the listing went without it (Apple, on a version never released).
 * `not_sent`: the line has no what's new, so nothing was offered.
 */
export const WHATS_NEW_STATUSES = ['sent', 'refused', 'not_sent'] as const
export const WhatsNewStatusSchema = z.enum(WHATS_NEW_STATUSES)
export type WhatsNewStatus = z.infer<typeof WhatsNewStatusSchema>

export const AppLineShippedWhatsNewSchema = z.object({
  /** The rendered text offered, `{schoolName}` filled in. */
  text: z.string().nullable(),
  status: WhatsNewStatusSchema
})
export type AppLineShippedWhatsNew = z.infer<typeof AppLineShippedWhatsNewSchema>

/** A draft a release covered, keyed by what the release read, so a newer save is never taken for it. */
export const AppLineReleasedDraftSchema = z.object({
  fieldKey: z.string(),
  savedAt: z.coerce.date()
})
export type AppLineReleasedDraft = z.infer<typeof AppLineReleasedDraftSchema>

/**
 * A build or OTA a release dispatched, completed by the workflow callback when
 * it binds the dispatch's artifact (U13 step 5). `manifest` is that artifact's
 * `payload.snapshotManifest`, copied: what the bundle was built from.
 */
export const LINE_RELEASE_DISPATCH_STATUSES = ['dispatched', 'succeeded', 'failed'] as const
export const LineReleaseDispatchStatusSchema = z.enum(LINE_RELEASE_DISPATCH_STATUSES)
export type LineReleaseDispatchStatus = z.infer<typeof LineReleaseDispatchStatusSchema>

export const AppLineShippedDispatchSchema = z.object({
  dispatchId: z.string(),
  /** `AppRelease.publicId` the dispatch hangs off. */
  releaseId: z.string().nullable(),
  ref: z.string(),
  status: LineReleaseDispatchStatusSchema,
  dispatchedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  /** `AppArtifact.publicId` the callback bound. */
  artifactId: z.string().nullable(),
  manifest: z.record(z.string(), z.unknown()).nullable(),
  /** Value drafts applied to their live homes before the dispatch. */
  appliedDrafts: z.array(AppLineReleasedDraftSchema),
  /** Status-only drafts this dispatch carries; stamped applied when it succeeds. */
  carriedDrafts: z.array(AppLineReleasedDraftSchema)
})
export type AppLineShippedDispatch = z.infer<typeof AppLineShippedDispatchSchema>

/**
 * `MobileAppReleaseLine.shippedSnapshot`. A baseline carries only the base;
 * a release adds the parts below, each written as the step that sends it
 * commits (KTD9). Every added key is optional, so a baseline row parses.
 *
 * A part is replaced only by a step that committed it: after a partial
 * release, a part whose step failed still says what the previous release sent.
 */
export const AppLineShippedSnapshotSchema = AppLineShippedSnapshotBaseSchema.extend({
  /** The store images sent, one entry per store. */
  images: z.array(AppLineShippedImagesSchema).optional(),
  whatsNew: AppLineShippedWhatsNewSchema.optional(),
  /** The App Store version the listing and screenshots were written to. */
  targetVersion: z.string().nullable().optional(),
  build: AppLineShippedDispatchSchema.optional(),
  ota: AppLineShippedDispatchSchema.optional(),
  release: AppLineReleaseRecordSchema.optional()
})
export type AppLineShippedSnapshot = z.infer<typeof AppLineShippedSnapshotSchema>

/** The token a line's what's new carries for the app's name (R2). */
export const WHATS_NEW_SCHOOL_NAME_TOKEN = '{schoolName}'

/**
 * A line's fleet-wide what's new for one app: every `{schoolName}` replaced
 * with the app's display name. Null or blank text renders null: there is
 * nothing to send.
 */
export function renderLineWhatsNew(text: string | null | undefined, schoolName: string): string | null {
  const trimmed = text?.trim()
  if (!trimmed) return null
  return trimmed.split(WHATS_NEW_SCHOOL_NAME_TOKEN).join(schoolName)
}

// -----------------------------------------------------------------------------
// The derivation (KTD3)
// -----------------------------------------------------------------------------

/** The line fields the derivation reads. */
export interface AppLineRef {
  /** `AppVersion.publicId`. */
  id: string
  runtimeVersion: string
}

/** The stored record fields the derivation reads, for one app, line and platform. */
export interface AppLineRecordInput {
  /** `AppVersion.publicId` of the record's line. */
  lineId: string
  platform: Platform
  planned: boolean
  blockedReason: string | null
  shippedVersionString: string | null
  shippedBuildNumber: string | null
  shippedAt: Date | null
  /** The last drift check (U15), carried onto the answer untouched. */
  drift?: AppLineDrift | null
}

export interface AppLineStateInput {
  platform: Platform
  line: AppLineRef
  /** This app's record for the line and platform, or null when it has none. */
  record: AppLineRecordInput | null
  /**
   * EVERY store version for this app and platform, on every line. The other
   * lines' rows decide `superseded`, and unresolved rows decide `unknown`.
   */
  rows: readonly MobileAppStoreVersion[]
  /** This store's lane stamp from the ledger row, as `storeVersionStanding` takes it. */
  lane?: StoreVersionLane | null
  /** A parameter, never a clock read, so every caller agrees. */
  now: Date
  thresholds?: StoreVersionThresholds
}

export interface AppPlatformLinesInput {
  platform: Platform
  /** Every line the fleet has. Lines this app has no record or version on are left out of the answer. */
  lines: readonly AppLineRef[]
  /** This app's records; rows for the other platform are ignored. */
  records: readonly AppLineRecordInput[]
  rows: readonly MobileAppStoreVersion[]
  lane?: StoreVersionLane | null
  now: Date
  thresholds?: StoreVersionThresholds
}

/**
 * `rolling_out` counts as live with a caveat and `halted` does not, the store
 * version board's rule (KTD3).
 */
const isLiveish = (row: MobileAppStoreVersion): boolean => row.storeState === 'live' || row.storeState === 'rolling_out'

/** The in-flight states that map onto a line state. `rolling_out` is live; `halted` and `other` are unknown. */
const IN_FLIGHT_LINE_STATES: Partial<Record<StoreVersionState, AppLineState>> = {
  preparing: 'preparing',
  submitted: 'in_review',
  in_review: 'in_review',
  // Approved and waiting for a release is past review but not live; R5 has no
  // separate word for it, and "in review" is the one that keeps it upcoming.
  approved_waiting: 'in_review',
  scheduled: 'in_review',
  rejected: 'rejected'
}

/** Newest ordering time wins, first-observed time breaks a tie: the board's own recency rule. */
const pickNewestRow = (rows: readonly MobileAppStoreVersion[]): MobileAppStoreVersion | undefined =>
  rows.reduce<MobileAppStoreVersion | undefined>((best, row) => {
    if (!best) return row
    const byOrdering = row.stateOrderingAt.getTime() - best.stateOrderingAt.getTime()
    if (byOrdering !== 0) return byOrdering > 0 ? row : best
    return row.firstObservedAt.getTime() >= best.firstObservedAt.getTime() ? row : best
  }, undefined)

const versionRef = (row: MobileAppStoreVersion | undefined): AppLineVersionRef | undefined =>
  row
    ? {
        ...(row.versionString ? { versionString: row.versionString } : {}),
        ...(row.buildNumber ? { buildNumber: row.buildNumber } : {}),
        storeState: row.storeState
      }
    : undefined

/** Numeric runtime order, so 1.10.0 is above 1.9.0. An unreadable runtime sorts as equal. */
export function compareLineRuntimes(a: string, b: string): number {
  return compareVersions({ version: a }, { version: b }) ?? 0
}

/**
 * Freshness of this platform's store evidence, the same gate `storeVersionStanding`
 * applies before it claims anything: a failed or stale read is `unknown`, never live.
 */
function evidenceGate(
  rows: readonly MobileAppStoreVersion[],
  lane: StoreVersionLane | null | undefined,
  now: Date,
  stalenessMs: number
): { reason: AppLineStateReason; reasonText?: string } | null {
  if (lane?.outcome === 'unreadable')
    return { reason: 'unreadable', ...(lane.reason ? { reasonText: lane.reason } : {}) }
  if (lane?.outcome === 'not-applicable') {
    return { reason: 'not_applicable', ...(lane.reason ? { reasonText: lane.reason } : {}) }
  }
  const laneReadable = lane?.outcome === 'ok' || lane?.outcome === 'empty'
  const candidates = [
    ...rows.map(r => r.observedAt),
    ...(laneReadable && lane?.lastCheckedAt ? [lane.lastCheckedAt] : [])
  ]
  if (candidates.length === 0) return { reason: 'never_observed' }
  const lastReadableAt = Math.max(...candidates.map(d => d.getTime()))
  if (now.getTime() - lastReadableAt > stalenessMs) return { reason: 'stale_observation' }
  return null
}

const shippedOf = (record: AppLineRecordInput | null): AppLineShipped | undefined =>
  record && (record.shippedVersionString || record.shippedBuildNumber)
    ? {
        versionString: record.shippedVersionString,
        buildNumber: record.shippedBuildNumber,
        shippedAt: record.shippedAt
      }
    : undefined

/**
 * THE app × line state derivation (R5, KTD3): one app, one line, one platform.
 *
 * Pure, with `now` a parameter, mirroring `storeVersionStanding`, so the board,
 * the school status and the CLI cannot disagree. Rules, in order:
 *
 *  1. Nothing on this line in the store, and no unresolved version that could be
 *     this line's: the record answers (`blocked` over `planned`), or `unknown`
 *     (`no_record`) when there is no planned record.
 *  2. The store read failed, was never made, or is older than the staleness
 *     threshold: `unknown`. Live is never claimed from old evidence.
 *  3. A live or in-flight version with no resolved runtime that could change this
 *     line's answer: `unknown` (`unresolved_runtime`). For a line that is live
 *     only a fully live unresolved version could supersede it; for any other line
 *     any open unresolved version could be its own.
 *  4. A newer line fully live on this platform: `superseded` (R7). A newer line
 *     that is only rolling out leaves this one live beside it.
 *  5. A live or rolling-out version on this line: `live`.
 *  6. An in-flight version on this line: its state, newest first. A resubmitted
 *     rejection comes back on the same store key, so it reads `in_review` again.
 *  7. Only closed versions: `superseded` (`line_closed`) when the store replaced
 *     them, else back to the record (`store_withdrawn`).
 *
 * The shipped values (R8) and the newest in-flight version ride along whatever
 * the state, so a superseded line still says what it shipped (R9).
 */
export function deriveAppLineState(input: AppLineStateInput): AppLineStateResult {
  const { platform, line, record, rows, lane, now } = input
  const stalenessMs = input.thresholds?.stalenessMs ?? STORE_VERSION_STALENESS_MS

  const lineRows = rows.filter(r => r.runtimeVersion === line.runtimeVersion)
  const lineLive = lineRows.filter(isLiveish)
  const lineInFlight = lineRows.filter(r => IN_FLIGHT_LINE_STATES[r.storeState] !== undefined)

  const base: LineStateBase = {
    lineId: line.id,
    runtimeVersion: line.runtimeVersion,
    platform,
    ...optional('live', versionRef(pickNewestRow(lineLive.filter(r => r.versionString)) ?? pickNewestRow(lineLive))),
    ...optional('inFlight', versionRef(pickNewestRow(lineInFlight))),
    ...optional('shipped', shippedOf(record)),
    ...optional('drift', record?.drift ?? undefined)
  }

  const unresolvedCouldChange = unresolvedCouldChangeLine(rows, lineLive.length > 0)

  // 1. Nothing observed on this line: intent is all there is, and intent is not an observation.
  if (lineRows.length === 0 && !unresolvedCouldChange) return fromRecord(base, record, 'no_store_version')

  // 2. The store evidence must be readable and fresh before it can claim anything.
  const gate = evidenceGate(rows, lane, now, stalenessMs)
  if (gate) return { ...base, state: 'unknown', ...gate }

  // 3. An open version we cannot place could be this line's.
  if (unresolvedCouldChange) return { ...base, state: 'unknown', reason: 'unresolved_runtime' }

  // 4. A newer line fully live on this platform supersedes this one (R7).
  if (newerLineLive(rows, line.runtimeVersion)) return { ...base, state: 'superseded', reason: 'newer_line_live' }

  // 5. Live, with the rollout caveat when nothing on the line is fully released.
  if (lineLive.length > 0) return liveAnswer(base, lineLive)

  // 6. The newest open version on the line decides, whatever its state.
  const newestOpen = pickNewestRow(lineRows.filter(r => isStoreVersionInFlight(r.storeState)))
  if (newestOpen) return inFlightAnswer(base, newestOpen)

  // 7. Only closed versions remain.
  if (lineRows.some(r => r.storeState === 'superseded')) return { ...base, state: 'superseded', reason: 'line_closed' }
  return fromRecord(base, record, 'store_withdrawn')
}

/** The fields every app × line answer carries, whatever its state. */
type LineStateBase = Omit<AppLineStateResult, 'state' | 'reason'>

/** The record answers when the store has nothing to say: `blocked` over `planned`, else `unknown`. */
function fromRecord(
  base: LineStateBase,
  record: AppLineRecordInput | null,
  reason: 'no_store_version' | 'store_withdrawn'
): AppLineStateResult {
  if (record?.blockedReason) return { ...base, state: 'blocked', reason, reasonText: record.blockedReason }
  if (record?.planned) return { ...base, state: 'planned', reason }
  return { ...base, state: 'unknown', reason: 'no_record' }
}

/**
 * Whether a version with no resolved runtime could change this line's answer. For
 * a live line only a fully live one could supersede it; otherwise any open one
 * could be its own.
 */
function unresolvedCouldChangeLine(rows: readonly MobileAppStoreVersion[], lineIsLive: boolean): boolean {
  const unresolved = rows.filter(r => !r.runtimeVersion)
  return lineIsLive
    ? unresolved.some(r => r.storeState === 'live')
    : unresolved.some(r => isLiveish(r) || isStoreVersionInFlight(r.storeState))
}

const newerLineLive = (rows: readonly MobileAppStoreVersion[], runtimeVersion: string): boolean =>
  rows.some(
    r => r.storeState === 'live' && !!r.runtimeVersion && compareLineRuntimes(r.runtimeVersion, runtimeVersion) > 0
  )

function liveAnswer(base: LineStateBase, lineLive: readonly MobileAppStoreVersion[]): AppLineStateResult {
  if (lineLive.some(r => r.storeState === 'live')) return { ...base, state: 'live', reason: 'store_live' }
  const fraction = pickNewestRow(lineLive)?.rolloutFraction
  return {
    ...base,
    state: 'live',
    reason: 'store_rolling_out',
    ...(fraction !== undefined && fraction !== null ? { rolloutFraction: fraction } : {})
  }
}

function inFlightAnswer(base: LineStateBase, newestOpen: MobileAppStoreVersion): AppLineStateResult {
  const mapped = IN_FLIGHT_LINE_STATES[newestOpen.storeState]
  if (mapped) return { ...base, state: mapped, reason: 'store_in_flight' }
  if (newestOpen.storeState === 'halted') return { ...base, state: 'unknown', reason: 'halted' }
  return { ...base, state: 'unknown', reason: 'unmapped_store_state', reasonText: newestOpen.vendorState }
}

/** `{ key: value }` when the value is present, `{}` otherwise, so optional keys stay absent on the wire. */
function optional<K extends string, V>(key: K, value: V | undefined): { [P in K]?: V } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: V }
}

const UPCOMING_STATES: ReadonlySet<AppLineState> = new Set(['preparing', 'in_review', 'rejected'])

/**
 * Every line one app has a record or a store version on, for one platform, plus
 * that platform's current and upcoming line (R6). iOS and Android are derived
 * separately because they sit on different lines through a whole rollout (AE4).
 *
 * BULK BY DESIGN. It reads only what it is handed, so a fleet summary loads
 * every record and store version once and calls this per app and platform in
 * memory; there is no per-app query behind it.
 *
 *  - `current` is the newest line in `live`. With the store unreadable, never
 *    read or stale, or a live version whose runtime is unresolved, it is
 *    `unknown` instead.
 *  - `upcoming` is the newest line ABOVE current that is preparing, in review or
 *    rejected. An unresolved in-flight version makes it `unknown` when no line
 *    already answers.
 */
export function deriveCurrentAndUpcoming(input: AppPlatformLinesInput): AppPlatformLines {
  const { platform, rows, lane, now, thresholds } = input
  const stalenessMs = thresholds?.stalenessMs ?? STORE_VERSION_STALENESS_MS
  const records = input.records.filter(r => r.platform === platform)
  const recordByLine = new Map(records.map(r => [r.lineId, r]))
  const runtimesSeen = new Set(rows.map(r => r.runtimeVersion).filter((r): r is string => !!r))

  const lines = input.lines
    .filter(line => recordByLine.has(line.id) || runtimesSeen.has(line.runtimeVersion))
    .map(line =>
      deriveAppLineState({
        platform,
        line,
        record: recordByLine.get(line.id) ?? null,
        rows,
        ...(lane !== undefined ? { lane } : {}),
        now,
        ...(thresholds ? { thresholds } : {})
      })
    )
    .sort((a, b) => compareLineRuntimes(b.runtimeVersion, a.runtimeVersion))

  const position = (result: AppLineStateResult): AppLinePosition => ({
    kind: 'line',
    lineId: result.lineId,
    runtimeVersion: result.runtimeVersion,
    state: result.state
  })

  // The same gate the per-line rule applies: with no fresh read, "no current
  // line" is as unprovable as "current 1.8".
  const gate = evidenceGate(rows, lane, now, stalenessMs)
  if (gate) {
    const unknown: AppLinePosition = { kind: 'unknown', reason: gate.reason }
    return { platform, lines, current: unknown, upcoming: unknown }
  }

  const unresolvedLive = rows.some(r => !r.runtimeVersion && isLiveish(r))
  const unresolvedInFlight = rows.some(r => !r.runtimeVersion && isStoreVersionInFlight(r.storeState) && !isLiveish(r))

  const currentLine = lines.find(l => l.state === 'live')
  const current: AppLinePosition = unresolvedLive
    ? { kind: 'unknown', reason: 'unresolved_runtime' }
    : currentLine
      ? position(currentLine)
      : { kind: 'none' }

  const upcomingLine = lines.find(
    l =>
      UPCOMING_STATES.has(l.state) &&
      (!currentLine || compareLineRuntimes(l.runtimeVersion, currentLine.runtimeVersion) > 0)
  )
  const upcoming: AppLinePosition = upcomingLine
    ? position(upcomingLine)
    : unresolvedInFlight || unresolvedLive
      ? { kind: 'unknown', reason: 'unresolved_runtime' }
      : { kind: 'none' }

  return { platform, lines, current, upcoming }
}

// -----------------------------------------------------------------------------
// The shipped-value rule (R8, KTD10)
// -----------------------------------------------------------------------------

const NUMERIC_BUILD = /^\d+$/

/**
 * Is the store version now live on a line newer than what the record says
 * shipped? The rule the shipped-value writer applies before its compare-and-set:
 *
 *  - Nothing recorded yet: newer.
 *  - The same build number: not newer (a re-observation, not a new ship).
 *  - Both build numbers numeric: the higher one. EAS counts build numbers per
 *    app (`appVersionSource: remote`), so a rebuild at the same marketing
 *    version still moves forward (AE3).
 *  - Otherwise: the later observation, the candidate's `stateOrderingAt` against
 *    the record's `shippedAt`.
 */
export function isNewerShippedBuild(
  candidate: { buildNumber: string; stateOrderingAt: Date },
  stored: { buildNumber: string | null; shippedAt: Date | null }
): boolean {
  if (!stored.buildNumber) return true
  if (candidate.buildNumber === stored.buildNumber) return false
  if (NUMERIC_BUILD.test(candidate.buildNumber) && NUMERIC_BUILD.test(stored.buildNumber)) {
    return Number(candidate.buildNumber) > Number(stored.buildNumber)
  }
  return !stored.shippedAt || candidate.stateOrderingAt.getTime() > stored.shippedAt.getTime()
}

// =============================================================================
// Compliance sign-offs (U14, R2, R23, KTD11)
//
// The compliance ANSWERS stay in code: they already vary by git ref, and
// several can only be applied in a browser or by hand in a store console. A
// line records, per platform and field, who signed off that its answers were
// applied, and by which lane. A submission on a line is refused until the line
// has an unrevoked sign-off for every field on that platform. Export
// compliance is not a field here: it is build-time code in
// `apps/mobile/app.config.ts`.
// =============================================================================

/**
 * The compliance fields per platform, each a closed union.
 *
 *  - Apple `privacy`: the App Privacy labels. Per app with no API; applied by
 *    the browser runner (`apps/browser-runner/src/steps/asc/privacy.ts`).
 *  - Apple `age_rating`: the age rating declaration.
 *  - Play `data_safety`: saved by the browser runner
 *    (`apps/browser-runner/src/steps/play/data-safety.ts`), published by a person.
 *  - Play `content_rating` and `declarations`: Play Console only.
 */
export const APPLE_COMPLIANCE_FIELDS = ['privacy', 'age_rating'] as const
export const PLAY_COMPLIANCE_FIELDS = ['data_safety', 'content_rating', 'declarations'] as const
export const COMPLIANCE_FIELDS = [...APPLE_COMPLIANCE_FIELDS, ...PLAY_COMPLIANCE_FIELDS] as const
export const ComplianceFieldSchema = z.enum(COMPLIANCE_FIELDS)
export type ComplianceField = z.infer<typeof ComplianceFieldSchema>

export const COMPLIANCE_FIELDS_BY_PLATFORM: Readonly<Record<Platform, readonly ComplianceField[]>> = {
  IOS: APPLE_COMPLIANCE_FIELDS,
  ANDROID: PLAY_COMPLIANCE_FIELDS
}

/** How a line's answers were applied: through a store API, by the browser runner, or by hand in the console. */
export const COMPLIANCE_LANES = ['api', 'browser_runner', 'manual'] as const
export const ComplianceLaneSchema = z.enum(COMPLIANCE_LANES)
export type ComplianceLane = z.infer<typeof ComplianceLaneSchema>

/** The words a refusal and the sign-off panel use for each field. */
export const COMPLIANCE_FIELD_LABEL: Readonly<Record<ComplianceField, string>> = {
  privacy: 'privacy labels',
  age_rating: 'age rating',
  data_safety: 'data safety',
  content_rating: 'content rating',
  declarations: 'declarations'
}

export function isComplianceFieldForPlatform(platform: Platform, field: ComplianceField): boolean {
  return COMPLIANCE_FIELDS_BY_PLATFORM[platform].includes(field)
}

/** Who signed or revoked, as the panel shows them. `null` when the user is gone or was never recorded. */
export const ComplianceActorSchema = z.object({ id: z.string(), name: z.string() }).nullable()
export type ComplianceActor = z.infer<typeof ComplianceActorSchema>

export const ComplianceSignoffSchema = z.object({
  id: z.string(),
  lineId: z.string(),
  platform: PlatformSchema,
  field: ComplianceFieldSchema,
  lane: ComplianceLaneSchema,
  signedBy: ComplianceActorSchema,
  signedAt: z.coerce.date(),
  note: z.string().nullable(),
  /** Copied forward from the previous line when this line was created. */
  carried: z.boolean(),
  revokedAt: z.coerce.date().nullable(),
  revokedBy: ComplianceActorSchema
})
export type ComplianceSignoff = z.infer<typeof ComplianceSignoffSchema>

/**
 * One field's standing on a line:
 *  - `signed`: signed off on this line.
 *  - `carried`: copied from the previous line and not revoked. Counts as signed.
 *  - `revoked`: staff revoked it because the answer changed. Blocks submissions.
 *  - `missing`: never signed off on this line. Blocks submissions.
 */
export const COMPLIANCE_FIELD_STATES = ['signed', 'carried', 'revoked', 'missing'] as const
export const ComplianceFieldStateSchema = z.enum(COMPLIANCE_FIELD_STATES)
export type ComplianceFieldState = z.infer<typeof ComplianceFieldStateSchema>

export const LineComplianceFieldSchema = z.object({
  platform: PlatformSchema,
  field: ComplianceFieldSchema,
  state: ComplianceFieldStateSchema,
  signoff: ComplianceSignoffSchema.nullable()
})
export type LineComplianceField = z.infer<typeof LineComplianceFieldSchema>

type ComplianceStanding = Pick<ComplianceSignoff, 'platform' | 'field' | 'carried' | 'revokedAt'>

function complianceFieldState(signoff: ComplianceStanding | undefined): ComplianceFieldState {
  if (!signoff) return 'missing'
  if (signoff.revokedAt) return 'revoked'
  return signoff.carried ? 'carried' : 'signed'
}

/**
 * Every field on both platforms with its standing, in the catalog's order. The
 * one derivation the gate, the endpoint and the panel share. A row for a field
 * the platform does not have is ignored.
 */
export function deriveLineCompliance(
  signoffs: readonly ComplianceStanding[]
): Array<{ platform: Platform; field: ComplianceField; state: ComplianceFieldState }> {
  return (['IOS', 'ANDROID'] as const).flatMap(platform =>
    COMPLIANCE_FIELDS_BY_PLATFORM[platform].map(field => {
      const signoff = signoffs.find(row => row.platform === platform && row.field === field)
      return { platform, field, state: complianceFieldState(signoff) }
    })
  )
}

/** The fields on `platform` that block a submission: missing or revoked. */
export function missingComplianceFields(
  platform: Platform,
  signoffs: readonly ComplianceStanding[]
): ComplianceField[] {
  return deriveLineCompliance(signoffs)
    .filter(entry => entry.platform === platform && (entry.state === 'missing' || entry.state === 'revoked'))
    .map(entry => entry.field)
}
